import { Box, Chip, MenuItem, Select, SelectChangeEvent, Slider, Switch, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import FilterBox from "./Components/FilterBox";
import SectionHeader from "./Components/SectionHeader";
import { useQuery } from "@tanstack/react-query";
import { CoachFilters, Country, Skill } from "../../../types";
import { fetchCountries, getAdvancedSearchLists, getSkillsLists } from "../../../api/network";
import {
  CheckedIcon,
  OverviewIcon,
  PlayingLevelIcon,
  ProfessionalInfoIcon,
  SkillsIcon,
  UnCheckedIcon,
} from "../../../assets/icons/MUI/AdvancedSearch";
import { colors } from "../../../theme/palette";
import { useTranslation } from "react-i18next";

type Formation = {
  id: number;
  name: string;
};

type CoachType = {
  id: number;
  name: string;
};

type Filters = {
  countriesCoachedIn: string;
  coachType: string;
  teamsCoachedRange: number[];
  trophiesRange: number[];
  hasFootballExperience: boolean;
  hasCertificateOrLicense: boolean;
  skills: number[];
  preferredFormation: string[];
};

const CoachFilter = ({
  advancedFilters,
  setAdvancedFilters,
  isFilterCleared,
}: {
  advancedFilters: CoachFilters;
  setAdvancedFilters: React.Dispatch<React.SetStateAction<CoachFilters>>;
  isFilterCleared: boolean;
}) => {
  const { t } = useTranslation();

  const initialFilters: CoachFilters = {
    countriesCoachedIn: advancedFilters.countriesCoachedIn || "",
    coachType: advancedFilters.coachType || "",
    teamsCoachedRange: (advancedFilters.teamsCoachedRange as [number, number]) || ([1, 100] as [number, number]),
    trophiesRange: (advancedFilters.trophiesRange as [number, number]) || ([1, 30] as [number, number]),
    hasFootballExperience: advancedFilters.hasFootballExperience || false,
    hasCertificateOrLicense: advancedFilters.hasCertificateOrLicense || false,
    skills: advancedFilters.skills || ([] as number[]),
    preferredFormation: advancedFilters.preferredFormation || ([] as string[]),
  };

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    setAdvancedFilters(filters);
  }, [filters]);

  useEffect(() => {
    setFilters(initialFilters);
  }, [isFilterCleared]);

  const { data: skills } = useQuery<Skill[]>({
    queryKey: ["coachSkills"],
    queryFn: () => getSkillsLists("coach"),
    initialData: [] as Skill[],
    staleTime: 0,
  });

  const { data: countries } = useQuery<Country[]>({
    queryKey: ["countries"],
    queryFn: () => fetchCountries(),
    initialData: [] as Country[],
    staleTime: 0,
  });

  const { data: coachTypes } = useQuery<CoachType[]>({
    queryKey: ["coachTypes"],
    queryFn: () => getAdvancedSearchLists("coachType"),
    initialData: [] as CoachType[],
    staleTime: 0,
  });

  const { data: preferredFormations } = useQuery<Formation[]>({
    queryKey: ["formations"],
    queryFn: () => getAdvancedSearchLists("formation"),
    initialData: [] as Formation[],
    staleTime: 0,
  });

  type FilterKeys = keyof Filters;

  const handleSliderChange = (name: FilterKeys) => (event: Event, newValue: number | number[]) => {
    setFilters((prevState) => ({ ...prevState, [name]: newValue }));
  };

  const handleSwitchChange = (name: FilterKeys) => () => {
    setFilters((prevState) => ({ ...prevState, [name]: !prevState[name] }));
  };

  const handleSkillClick = (skill: number) => () => {
    setFilters((prevState) => {
      const newSkills = (prevState.skills || []).includes(skill)
        ? (prevState.skills || []).filter((s) => s !== skill)
        : [...(prevState.skills || []), skill];
      return { ...prevState, skills: newSkills };
    });
  };

  const handlePlayingLevelClick = (level: string) => () => {
    const newLevels = (filters.preferredFormation || []).includes(level)
      ? (filters.preferredFormation || []).filter((l) => l !== level)
      : [...(filters.preferredFormation || []), level];
    setFilters((prevState) => ({ ...prevState, preferredFormation: newLevels }));
  };

  return (
    <>
      <SectionHeader icon={OverviewIcon} title={t("components.networkController.advancedFilterPopUp.coachOverview")} />
      <FilterBox label={t("components.networkController.advancedFilterPopUp.countriesCoachedIn")}>
        <Select
          value={filters.countriesCoachedIn}
          onChange={(event: SelectChangeEvent<string>) =>
            setFilters((prevState) => ({
              ...prevState,
              countriesCoachedIn: event.target.value as string,
            }))
          }
          displayEmpty
          sx={{ minWidth: "160px" }}
        >
          {countries?.map((country) => (
            <MenuItem key={country?.id} value={country?.code}>
              {country?.name}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>

      <FilterBox label={t("components.networkController.advancedFilterPopUp.coachType")}>
        <Select
          value={filters.coachType}
          onChange={(event: SelectChangeEvent<string>) =>
            setFilters((prevState) => ({
              ...prevState,
              coachType: event.target.value as string,
            }))
          }
          displayEmpty
          sx={{ minWidth: "160px" }}
        >
          {coachTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>

      <FilterBox label={t("components.networkController.advancedFilterPopUp.teamsCoachedRange")}>
        <Slider
          value={filters.teamsCoachedRange as [number, number]}
          onChange={handleSliderChange("teamsCoachedRange")}
          valueLabelDisplay="auto"
          getAriaValueText={(value: number) => `${value} Teams`}
          valueLabelFormat={(value: number) => `${value} Teams`}
          sx={{ maxWidth: "360px", width: "50%" }}
          min={1}
          max={100}
        />
      </FilterBox>

      <FilterBox label={t("components.networkController.advancedFilterPopUp.trophiesRange")}>
        <Slider
          value={filters.trophiesRange as [number, number]}
          onChange={handleSliderChange("trophiesRange")}
          valueLabelDisplay="auto"
          getAriaValueText={(value: number) => `${value} Trophies`}
          valueLabelFormat={(value: number) => `${value} Trophies`}
          sx={{ maxWidth: "360px", width: "50%" }}
          min={0}
          max={30}
        />
      </FilterBox>

      <SectionHeader
        icon={ProfessionalInfoIcon}
        title={t("components.networkController.advancedFilterPopUp.professionalInfo")}
      />
      <FilterBox label={t("components.networkController.advancedFilterPopUp.hasFootballExperience")}>
        <Switch checked={filters.hasFootballExperience} onChange={handleSwitchChange("hasFootballExperience")} />
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.hasCertificateOrLicense")}>
        <Switch checked={filters.hasCertificateOrLicense} onChange={handleSwitchChange("hasCertificateOrLicense")} />
      </FilterBox>

      <SectionHeader icon={SkillsIcon} title={t("components.networkController.advancedFilterPopUp.skills")} />
      <Box
        sx={{
          paddingBottom: "30px",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "20px",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 12px",
        }}
      >
        {skills.map((skill) => (
          <Chip
            key={skill.id}
            color={filters.skills.includes(skill.id) ? "primary" : "secondary"}
            label={skill.name}
            icon={
              filters.skills.includes(skill.id) ? (
                <CheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              ) : (
                <UnCheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              )
            }
            onClick={handleSkillClick(skill.id)}
            sx={{
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "400px",
              ...(filters.skills.includes(skill.id) ? {} : { backgroundColor: colors.gray50, color: colors.black }),
            }}
          />
        ))}
      </Box>

      <SectionHeader
        icon={PlayingLevelIcon}
        title={t("components.networkController.advancedFilterPopUp.preferredFormation")}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "8px",
          rowGap: "12px",
        }}
      >
        {preferredFormations.map((formation) => (
          <Chip
            key={formation.id}
            color={filters.preferredFormation.includes(formation.name) ? "primary" : "secondary"}
            label={formation.name}
            icon={
              filters.preferredFormation.includes(formation.name) ? (
                <CheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              ) : (
                <UnCheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              )
            }
            onClick={handlePlayingLevelClick(formation.name)}
            sx={{
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "400px",
              ...(filters.preferredFormation.includes(formation.name)
                ? {}
                : { backgroundColor: colors.gray50, color: colors.black }),
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default CoachFilter;
